import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/molecules/Dialog';
import { Button } from '@/components/atoms/Button';
import { Select } from '@/components/atoms/Select';
import {
  invitationsAPI,
  type CreateInvitationsResult,
  type InvitationRole,
} from '@/services/api/invitations.api';
import styles from './InviteDialog.module.scss';

export interface InviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Empresa fija a la que se invita (backoffice detalle de empresa). Para
   *  company_admin se omite (el backend usa su propia empresa). */
  companyId?: string;
  /** Si se pasa (y no hay companyId fijo), muestra un selector de empresa
   *  obligatorio (backoffice → Usuarios). */
  companies?: Array<{ id: string; name: string }>;
  /** Backoffice: permite elegir el rol (Student / Company admin). */
  allowRoleSelection?: boolean;
  /** Se llama cuando se ha enviado al menos una invitación (para refrescar la lista). */
  onInvited?: () => void;
}

const REASON_LABELS: Record<string, string> = {
  already_registered: 'already registered',
  invalid: 'invalid email',
  email_failed: "couldn't send the email",
};

function parseEmails(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(/[\s,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
    ),
  ];
}

export function InviteDialog({
  open,
  onOpenChange,
  companyId,
  companies,
  allowRoleSelection = false,
  onInvited,
}: InviteDialogProps) {
  const [value, setValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<CreateInvitationsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [role, setRole] = useState<InvitationRole>('student');

  const emails = parseEmails(value);
  // El selector de empresa aplica solo si no hay empresa fija y se pasó la lista.
  const needsCompanyPick = !companyId && !!companies;
  const canSend = emails.length > 0 && (!needsCompanyPick || !!selectedCompanyId);

  const reset = () => {
    setValue('');
    setResult(null);
    setError(null);
    setIsSending(false);
    setSelectedCompanyId('');
    setRole('student');
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSend = async () => {
    if (!canSend) return;
    setIsSending(true);
    setError(null);
    try {
      const targetCompanyId = companyId ?? (needsCompanyPick ? selectedCompanyId : undefined);
      const res = await invitationsAPI.create(
        emails,
        targetCompanyId,
        allowRoleSelection ? role : undefined
      );
      setResult(res);
      setValue('');
      if (res.invited.length > 0) onInvited?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite students</DialogTitle>
          <DialogDescription>
            Enter one or more email addresses (separated by commas, spaces or new lines). Each
            person gets a link to activate their account.
          </DialogDescription>
        </DialogHeader>

        <div className={styles.inviteDialog__body}>
          {needsCompanyPick && (
            <div className={styles.inviteDialog__field}>
              <label className={styles.inviteDialog__label}>Company</label>
              <Select
                placeholder="Select a company"
                value={selectedCompanyId}
                onValueChange={setSelectedCompanyId}
                options={(companies ?? []).map((c) => ({ label: c.name, value: c.id }))}
              />
            </div>
          )}

          {allowRoleSelection && (
            <div className={styles.inviteDialog__field}>
              <label className={styles.inviteDialog__label}>Role</label>
              <Select
                value={role}
                onValueChange={(v) => setRole(v as InvitationRole)}
                options={[
                  { label: 'Student', value: 'student' },
                  { label: 'Company admin', value: 'company_admin' },
                ]}
              />
            </div>
          )}

          <textarea
            className={styles.inviteDialog__textarea}
            placeholder="ana@empresa.com, juan@empresa.com"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={4}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            aria-label="Email addresses to invite"
          />
          {emails.length > 0 && (
            <p className={styles.inviteDialog__count}>
              {emails.length} email{emails.length > 1 ? 's' : ''} ready
            </p>
          )}

          {error && (
            <p className={styles.inviteDialog__error} role="alert">
              {error}
            </p>
          )}

          {result && (
            <div className={styles.inviteDialog__result}>
              {result.invited.length > 0 && (
                <p className={styles.inviteDialog__resultOk}>
                  {result.invited.length} invitation{result.invited.length > 1 ? 's' : ''} sent.
                </p>
              )}
              {result.skipped.length > 0 && (
                <>
                  <p className={styles.inviteDialog__resultSkippedTitle}>
                    {result.skipped.length} skipped:
                  </p>
                  <ul className={styles.inviteDialog__skipped}>
                    {result.skipped.map((s) => (
                      <li key={s.email}>
                        {s.email} — {REASON_LABELS[s.reason] ?? s.reason}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)}>
            {result ? 'Done' : 'Cancel'}
          </Button>
          <Button variant="primary" onClick={handleSend} isLoading={isSending} disabled={!canSend}>
            Send invitations
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default InviteDialog;
